/**
 * The seed's flight, its gathering, and the guarantee that every attempt ends.
 *
 * This file is deliberately free of any rendering. It is a plain simulation, so
 * the throw the player releases and the trajectory the seed actually flies come
 * from the same code, and so the whole thing can be tested and swept without a
 * browser.
 *
 * The shape of an attempt:
 *
 * 1. **Moving.** The seed turns, slows on the cloth, and bounces off the walls.
 *    It gathers only what it touches, and absorbs from a well that charges as it
 *    slows. Reaching the minimum while moving does NOT end the attempt, because
 *    the destination still matters.
 * 2. **Settling.** Once the seed has stopped, the well it was already charging
 *    keeps swelling around it, drawing in ever more of the field. There are no
 *    stages and no switch at the stop: one charge decides both the reach and the
 *    pull, and it only ever grows. The only bound is the attempt ceiling, at
 *    which the nearest remaining pixels are streamed in so that every attempt
 *    ends. The stream moves real pixels and keeps their real colours.
 *
 * Every pixel is gathered at most once, and gathering during movement is swept
 * along the segments the seed actually travelled, split at each bounce, so a
 * fast seed cannot pass through a pixel it never touched.
 */

import { COLLECT_CONFIG } from './collectConfig';
import { COLOUR_ANCHORS, emptyMixture, type ColourMixture } from './colour';
import {
  buildGrid,
  floatOffsetX,
  floatOffsetY,
  type CollectBoard,
  type PlayableRect,
  type SpatialGrid,
} from './collectBoard';
import type { ContinentId } from './types';

/** The seed's motion, as a value. Kept separate so the preview can reuse it. */
export interface MotionState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  spin: number;
  /**
   * How far the seed's heading has turned since it was released, in radians.
   *
   * Turned rather than drawn separately, because this is what lets the seed be
   * drawn spinning at the rate it is actually spinning: the angle is the integral
   * of the spin, so its rate of change is the spin at this moment, whatever the
   * spin is doing.
   */
  rotation: number;
}

export interface LaunchParameters {
  /** Logical units per second at the moment of release. */
  readonly speed: number;
  /** Radians. Zero points right and positive angles point down the screen. */
  readonly angle: number;
  /** Radians per second. Positive turns clockwise on screen. */
  readonly spin: number;
  /**
   * Where the seed is released from, in board logical units.
   *
   * Omitted means the board's own spawn point, which is what the sweep and the
   * tests want. The mini-game sets it, because the player drags the seed to
   * wherever they let go and it flies on from exactly there.
   */
  readonly origin?: { readonly x: number; readonly y: number };
}

export type CollectPhase = 'moving' | 'settling' | 'finished';

export interface CollectDiagnostics {
  /** Gathered while the seed was still travelling. */
  readonly travelCollected: number;
  /** Gathered after the seed stopped. */
  readonly settlingCollected: number;
  /** Gathered by being touched directly. */
  readonly contactCollected: number;
  /** Gathered by being pulled in without being touched first. */
  readonly attractionCollected: number;
  /**
   * Gathered by the last-resort stream at the attempt ceiling.
   *
   * Zero on almost every attempt. A non-zero value means the board did not have
   * the minimum's colours anywhere the seed's absorption could reach, so the
   * ceiling had to finish the attempt.
   */
  readonly streamedCollected: number;
  readonly durationSeconds: number;
  readonly bounces: number;
  readonly stoppedAfterSeconds: number;
}

export interface CollectResult {
  readonly boardSeed: number;
  readonly continent: ContinentId;
  /** Gathered count per anchor index. Any-colour pixels are not in here. */
  readonly counts: ReadonlyMap<number, number>;
  readonly mixture: ColourMixture;
  readonly totalCollected: number;
  /**
   * Any-colour pixels gathered from the field. The seed's own any-colour pixel is
   * not included; `anyColourPixels` is the number that matters.
   */
  readonly wildcardsGathered: number;
  /**
   * Every any-colour pixel the collection carries, the seed's own included.
   *
   * The player's seed is itself one of these from the moment it is launched, so
   * this is never zero and an encounter always has something to work with.
   */
  readonly anyColourPixels: number;
  readonly minimumRequired: number;
  readonly launch: LaunchParameters;
  readonly diagnostics: CollectDiagnostics;
}

/** The serialisable form stored on a battle episode. */
export function resultToRecipe(result: CollectResult): {
  boardSeed: number;
  minimumRequired: number;
  totalCollected: number;
  anyColourPixels: number;
  launch: LaunchParameters;
  colours: Array<{ anchorId: string; hex: string; count: number; share: number }>;
} {
  const colours = [...result.counts.entries()]
    .map(([anchorIndex, count]) => ({
      anchorId: COLOUR_ANCHORS[anchorIndex]!.id,
      hex: COLOUR_ANCHORS[anchorIndex]!.hex,
      count,
      share: result.totalCollected > 0 ? count / result.totalCollected : 0,
    }))
    .sort((a, b) => b.count - a.count);
  return {
    boardSeed: result.boardSeed,
    minimumRequired: result.minimumRequired,
    totalCollected: result.totalCollected,
    anyColourPixels: result.anyColourPixels,
    launch: result.launch,
    colours,
  };
}

/**
 * Applies one cushion contact to the seed.
 *
 * Real cloth-cushion contact does two things that the plain reflection cannot:
 *
 * - The contact point of a spinning ball is travelling sideways as the ball
 *   touches, and the cushion drags on it. The drag is what deflects the rebound
 *   toward or away from the cushion depending on the spin - running side and
 *   check side. It is modelled by driving the contact-point slip toward zero, so
 *   the tangential speed and the spin move together, each limited by cushion
 *   friction.
 * - The contact spends most of the side spin. That is what keeps a spinning ball
 *   from being steered back into the same cushion again and again.
 *
 * `normal` is the outward unit normal of the cushion that was hit, and `incoming`
 * is the normal speed just before the reflection, which sets how much grip the
 * contact can exert.
 */
function cushionContact(state: MotionState, normalX: number, normalY: number, incoming: number, restitution: number): void {
  // The tangent runs a quarter turn from the normal, so sliding is measured
  // along the cushion and positive in one consistent direction.
  const tangentX = -normalY;
  const tangentY = normalX;
  const tangential = state.vx * tangentX + state.vy * tangentY;
  // The material point at the contact slides along the cushion at the ball's own
  // tangential speed, less the part the spin contributes. The arm that relates
  // the two is stated rather than assumed, because this game's spin is a
  // steering rate and has no radius of its own.
  const slip = tangential - state.spin * COLLECT_CONFIG.cushionSpinArmPx;
  // Two thirds of a full stop would come out of the ball's tangential travel and
  // one third out of its spin; only the share cushion friction allows is taken.
  const grip = COLLECT_CONFIG.cushionFriction * incoming * (1 + restitution);
  const impulse = Math.max(-grip, Math.min(grip, -slip / 3));
  state.vx += impulse * tangentX;
  state.vy += impulse * tangentY;
  // The contact spends side spin, and only ever spends it. The impulse is not fed
  // back into the spin as a torque: this game's spin is a heading rate rather
  // than a rolling rate, so a torque computed from the contact radius would be
  // wrong by a large factor and could manufacture spin - or reverse it, which
  // steers the seed back into the cushion it has just left.
  state.spin *= COLLECT_CONFIG.cushionSpinRetention;
}

/**
 * Moves the seed for one step, resolving wall collisions as it goes.
 *
 * Rules that matter:
 * - Turning rotates the velocity, which changes direction without adding speed.
 *   The rate is the spin, so the angle turned is the spin integrated over the
 *   flight; `state.rotation` accumulates it so the seed can be drawn spinning.
 *   The speed is then reduced according to the surface model.
 * - A bounce multiplies only the part of the velocity that hits the wall by the
 *   restitution, and reverses it. The along-the-wall part is untouched. Below a
 *   restitution of 1 that loses speed; only at exactly 1 is speed conserved.
 * - The path is written into `segments` as a flat list of `x0,y0,x1,y1` runs,
 *   split at every bounce, because gathering must follow the route actually
 *   travelled rather than a straight line across the bounces.
 *
 * Returns the number of bounces.
 */
export function advanceMotion(
  state: MotionState,
  dt: number,
  bounds: PlayableRect,
  radius: number,
  restitution: number,
  segments: number[],
): number {
  segments.length = 0;
  const turn = state.spin * dt;
  if (turn !== 0) {
    const cos = Math.cos(turn);
    const sin = Math.sin(turn);
    const vx = state.vx * cos - state.vy * sin;
    const vy = state.vx * sin + state.vy * cos;
    state.vx = vx;
    state.vy = vy;
    state.rotation += turn;
  }

  // Cloth takes speed off a rolling ball at a roughly constant rate, so the seed
  // reaches a stop in finite time rather than decaying towards one.
  const speed = Math.hypot(state.vx, state.vy);
  if (speed > 0) {
    const remaining = Math.max(0, speed - COLLECT_CONFIG.tableFriction * dt);
    const scale = remaining / speed;
    state.vx *= scale;
    state.vy *= scale;
  }
  // The spin is spent more slowly than the speed, so the curve lasts well into
  // the run instead of being over in the first moment. It is still spent faster
  // than the speed, which is what keeps the radius of curvature growing rather
  // than collapsing as the seed slows.
  state.spin *= Math.exp(-COLLECT_CONFIG.clothSpinDecayPerSecond * dt);
  // The safety net. The seed stops in finite time, so the total turning is
  // already bounded; this only catches a combination of dials that would
  // otherwise coil the seed in place for an unbounded time.
  if (Math.abs(state.rotation) >= COLLECT_CONFIG.maxTurnRadians) state.spin = 0;

  const minX = bounds.left + radius;
  const maxX = bounds.right - radius;
  const minY = bounds.top + radius;
  const maxY = bounds.bottom - radius;

  let remaining = dt;
  let bounces = 0;
  let fromX = state.x;
  let fromY = state.y;
  // The guard bounds the loop even if a corner produces several contacts in a
  // single step. It is generous: a normal step makes at most one or two.
  for (let guard = 0; guard < 12 && remaining > 1e-9; guard += 1) {
    let timeX = Number.POSITIVE_INFINITY;
    let timeY = Number.POSITIVE_INFINITY;
    if (state.vx < 0) timeX = (minX - state.x) / state.vx;
    else if (state.vx > 0) timeX = (maxX - state.x) / state.vx;
    if (state.vy < 0) timeY = (minY - state.y) / state.vy;
    else if (state.vy > 0) timeY = (maxY - state.y) / state.vy;

    const hitX = timeX >= -1e-9 && timeX <= remaining;
    const hitY = timeY >= -1e-9 && timeY <= remaining;
    if (!hitX && !hitY) {
      state.x += state.vx * remaining;
      state.y += state.vy * remaining;
      remaining = 0;
      break;
    }

    const step = Math.max(0, Math.min(hitX ? timeX : Number.POSITIVE_INFINITY, hitY ? timeY : Number.POSITIVE_INFINITY));
    state.x += state.vx * step;
    state.y += state.vy * step;
    remaining -= step;
    bounces += 1;

    if (hitX) {
      const hitLeftWall = state.vx < 0;
      const normalX = hitLeftWall ? 1 : -1;
      const incoming = Math.abs(state.vx);
      state.vx = -state.vx * restitution;
      cushionContact(state, normalX, 0, incoming, restitution);
      // Placed exactly on the wall it hit, so a step landing on the boundary
      // cannot register as a second contact with the same wall.
      state.x = hitLeftWall ? minX : maxX;
    }
    if (hitY) {
      const hitTopWall = state.vy < 0;
      const normalY = hitTopWall ? 1 : -1;
      const incoming = Math.abs(state.vy);
      state.vy = -state.vy * restitution;
      cushionContact(state, 0, normalY, incoming, restitution);
      state.y = hitTopWall ? minY : maxY;
    }
    segments.push(fromX, fromY, state.x, state.y);
    fromX = state.x;
    fromY = state.y;
  }
  if (remaining > 0) {
    state.x += state.vx * remaining;
    state.y += state.vy * remaining;
  }
  segments.push(fromX, fromY, state.x, state.y);
  return bounces;
}

/** Squared distance from a point to a segment. */
export function distanceSqToSegment(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq <= 1e-12) return (px - x0) ** 2 + (py - y0) ** 2;
  let t = ((px - x0) * dx + (py - y0) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const cx = x0 + t * dx;
  const cy = y0 + t * dy;
  return (px - cx) ** 2 + (py - cy) ** 2;
}

/**
 * Runs one attempt.
 *
 * The caller drives it with real elapsed time; internally it uses a fixed step,
 * so the outcome does not depend on the frame rate, and no single call can run
 * away with the frame budget.
 */
export interface CollectSimulationOptions {
  /**
   * Consider every pixel instead of using the grid. This exists so tests can
   * prove the grid optimisation changes nothing about the outcome, and so the
   * launch sweep can cross-check its measurements.
   */
  readonly exhaustiveCandidates?: boolean;
  /** Override the collection target. Defaults to the configured minimum, capped by the board. */
  readonly minimumRequired?: number;
}

export class CollectSimulation {
  readonly board: CollectBoard;
  readonly minimumRequired: number;
  readonly launch: LaunchParameters;
  private readonly positionX: Float64Array;
  private readonly positionY: Float64Array;
  private readonly velocityX: Float64Array;
  private readonly velocityY: Float64Array;
  private readonly alive: Uint8Array;
  private readonly detached: Uint8Array;
  private readonly contactRadius = COLLECT_CONFIG.contactRadius;

  /** Pixels no longer on the grid because they are being pulled toward the seed. */
  private readonly detachedList: number[] = [];
  /** Pixels chosen by the final stream, with where they started from. */
  private readonly streamingList: number[] = [];
  private readonly streamingFlag: Uint8Array;
  private readonly streamFromX: Float64Array;
  private readonly streamFromY: Float64Array;

  private readonly grid: SpatialGrid;
  private readonly exhaustive: boolean;
  private readonly candidates: number[] = [];
  private readonly segments: number[] = [];
  private readonly countsByAnchor = new Map<number, number>();

  private motion: MotionState;
  private phaseValue: CollectPhase = 'moving';
  private accumulator = 0;
  private clock = 0;
  private stoppedAfter = 0;
  private bounceCount = 0;
  private finish?: CollectResult;
  /** How far the absorption well has charged, 0 to 1. Only ever grows. */
  private absorptionCharge = 0;

  private travelCollected = 0;
  private settlingCollected = 0;
  private contactCollected = 0;
  private attractionCollected = 0;
  private streamedCollected = 0;
  /** Field pixels gathered that are any-colour rather than a single anchor. */
  private wildcardGatheredCount = 0;

  constructor(board: CollectBoard, launch: LaunchParameters, options: CollectSimulationOptions = {}) {
    this.board = board;
    this.launch = launch;
    this.exhaustive = options.exhaustiveCandidates === true;
    const count = board.pixels.length;
    this.positionX = new Float64Array(count);
    this.positionY = new Float64Array(count);
    this.velocityX = new Float64Array(count);
    this.velocityY = new Float64Array(count);
    this.alive = new Uint8Array(count).fill(1);
    this.detached = new Uint8Array(count);
    this.streamingFlag = new Uint8Array(count);
    this.streamFromX = new Float64Array(count);
    this.streamFromY = new Float64Array(count);
    for (let index = 0; index < count; index += 1) {
      const pixel = board.pixels[index]!;
      this.positionX[index] = pixel.homeX;
      this.positionY[index] = pixel.homeY;
    }
    this.grid = buildGrid(board.pixels, board.playable, board.spacing, board.grid.maxFloatAmplitude);
    // The minimum can never exceed what exists, so completion is always possible.
    const requested = options.minimumRequired ?? COLLECT_CONFIG.minimumCollected;
    this.minimumRequired = Math.min(requested, count);
    const origin = launch.origin ?? board.spawn;
    this.motion = {
      x: origin.x,
      y: origin.y,
      vx: Math.cos(launch.angle) * launch.speed,
      vy: Math.sin(launch.angle) * launch.speed,
      spin: launch.spin,
      rotation: 0,
    };
  }

  get phase(): CollectPhase {
    return this.phaseValue;
  }

  /** The seed's position, velocity, spin and turned angle. Read-only by convention; the scene only reads it. */
  get seed(): MotionState {
    return this.motion;
  }

  get collected(): number {
    let total = this.wildcardGatheredCount;
    for (const value of this.countsByAnchor.values()) total += value;
    return total;
  }

  /** Any-colour pixels gathered from the field, the seed's own not included. */
  get wildcardsGathered(): number {
    return this.wildcardGatheredCount;
  }

  /**
   * Every any-colour pixel the collection carries, the seed's own included.
   *
   * The seed the player launches is itself a pixel of every colour, which is what
   * guarantees a reveal always has at least one and never has to fall back on a
   * floor or refuse the draw.
   */
  get anyColourPixels(): number {
    return 1 + this.wildcardGatheredCount;
  }

  /**
   * The gathered mixture so far, ready to compare against a profile mid-flight.
   *
   * Any-colour pixels are deliberately absent: they belong to no anchor, and they
   * enter the encounter separately as mass on every anchor. Counting them here
   * would distort the very colour the player aimed for.
   */
  mixture(): ColourMixture {
    const mixture = emptyMixture() as { mass: number[]; total: number };
    let total = 0;
    for (const [anchorIndex, count] of this.countsByAnchor) {
      mixture.mass[anchorIndex] = (mixture.mass[anchorIndex] ?? 0) + count;
      total += count;
    }
    mixture.total = total;
    return mixture;
  }

  /** How full the seed is, 0 to 1, against the minimum. */
  get fill(): number {
    return this.minimumRequired <= 0 ? 1 : Math.min(1, this.collected / this.minimumRequired);
  }

  get result(): CollectResult | undefined {
    return this.finish;
  }

  /** Total elapsed simulated time, in seconds. */
  get elapsed(): number {
    return this.clock;
  }

  /** Wall bounces so far, available while the attempt is running. */
  get bounces(): number {
    return this.bounceCount;
  }

  /** Whether a specific pixel has been gathered. Used by tests and the sweep harness. */
  wasGathered(index: number): boolean {
    return this.alive[index] === 0;
  }

  /** Whether a specific pixel has been pulled off its home but not yet gathered. */
  isDetached(index: number): boolean {
    return this.detached[index] === 1 || this.streamingFlag[index] === 1;
  }

  /**
   * Writes the gathered count per anchor into the caller's array.
   *
   * The live composition readout updates several times a second, so it reads the
   * counts through here rather than through `mixture()`. That keeps the readout
   * allocation-free, and the caller owns the array so it can be reused.
   */
  fillAnchorCounts(out: Int32Array): void {
    if (out.length < COLOUR_ANCHORS.length) {
      throw new Error(`Garaimon: an anchor count buffer must hold all ${COLOUR_ANCHORS.length} anchors, but was given ${out.length}.`);
    }
    out.fill(0);
    for (const [anchorIndex, count] of this.countsByAnchor) out[anchorIndex] = count;
  }

  /**
   * Copies every pixel's current position into the caller's buffers.
   *
   * The renderer needs the positions of detached pixels - the ones being pulled
   * toward the seed - which only the simulation knows. Copying into reused
   * buffers keeps the render loop free of allocation.
   */
  copyPositions(outX: Float64Array, outY: Float64Array): void {
    if (outX.length < this.positionX.length || outY.length < this.positionY.length) {
      throw new Error(`Garaimon: position buffers of ${outX.length} and ${outY.length} cannot hold the board's ${this.positionX.length} pixels.`);
    }
    outX.set(this.positionX);
    outY.set(this.positionY);
  }

  /**
   * Fills the candidate list for a point and radius.
   *
   * Normally this is a grid query. With `exhaustiveCandidates` it is every pixel
   * still on the board, which the tests use to check the grid returns a superset
   * of what a full scan would.
   */
  private fillCandidates(x: number, y: number, radius: number): number[] {
    if (!this.exhaustive) return collectCandidates(this.grid, this.candidates, x, y, radius);
    const out = this.candidates;
    out.length = 0;
    for (let index = 0; index < this.board.pixels.length; index += 1) {
      if (this.alive[index] && !this.detached[index] && !this.streamingFlag[index]) out.push(index);
    }
    return out;
  }

  /** Whether the attempt has ended. A getter rather than a field comparison, so narrowing cannot hide it. */
  get isFinished(): boolean {
    return this.phaseValue === 'finished';
  }

  /**
   * Advances the simulation by real elapsed time.
   *
   * The step count is capped, so a long stall such as a backgrounded tab cannot
   * make the next frame do an unbounded amount of work.
   */
  advance(elapsedSeconds: number): void {
    if (this.isFinished || !Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0) return;
    this.accumulator += elapsedSeconds;
    const step = COLLECT_CONFIG.fixedStepSeconds;
    let steps = 0;
    const maxSteps = 24;
    while (this.accumulator >= step && steps < maxSteps && !this.isFinished) {
      if (this.clock + step > COLLECT_CONFIG.maxAttemptSeconds) {
        // The stated ceiling is a promise, so the last resort is taken rather
        // than letting the attempt run on.
        this.enterStream();
        this.runStream(COLLECT_CONFIG.finalStreamSeconds);
        break;
      }
      this.stepOnce(step);
      this.accumulator -= step;
      steps += 1;
    }
    if (steps >= maxSteps) this.accumulator = 0;
  }

  private stepOnce(step: number): void {
    this.clock += step;
    this.advanceAbsorption(step);
    if (this.phaseValue === 'moving') this.stepMoving(step);
    else if (this.phaseValue === 'settling') this.stepSettling(step);
  }

  private stepMoving(step: number): void {
    this.bounceCount += advanceMotion(this.motion, step, this.board.playable, this.contactRadius, COLLECT_CONFIG.wallRestitution, this.segments);
    this.gatherAlongSegments();
    this.updateAttraction(step);
    if (this.phaseValue !== 'moving') return;
    const speed = Math.hypot(this.motion.vx, this.motion.vy);
    if (speed < COLLECT_CONFIG.stopSpeed) {
      this.motion.vx = 0;
      this.motion.vy = 0;
      this.motion.spin = 0;
      this.stoppedAfter = this.clock;
      this.phaseValue = 'settling';
      if (this.collected >= this.minimumRequired) this.finishNow();
    }
  }

  /**
   * Absorbing once the seed has stopped.
   *
   * Nothing here is staged. The reach and the pull are the same charge the
   * flight was already filling, and the charge keeps growing after the stop, so
   * the seed at rest sits at the centre of a well that visibly swells around it.
   * What bounds the wait is the attempt ceiling in `advance`, which streams the
   * nearest remaining pixels in if the board's colours are out of reach.
   */
  private stepSettling(step: number): void {
    this.updateAttraction(step);
    if (this.phaseValue === 'finished') return;
    if (this.collected >= this.minimumRequired) this.finishNow();
  }

  /**
   * The absorption reach right now, which swells as the well charges.
   *
   * One formula for the whole attempt, flight and rest alike. There is no
   * separate settling step, no radius that appears only once the seed has
   * stopped, and nothing that switches: the reach is simply the charge, and the
   * charge only ever grows.
   */
  attractionRadius(): number {
    return this.contactRadius + (COLLECT_CONFIG.maxAttractionRadius - this.contactRadius) * this.absorptionCharge;
  }

  /** How much pull is in force right now, read from the same charge as the reach. */
  attractionStrength(): number {
    return COLLECT_CONFIG.maxAttractionStrength * this.absorptionCharge;
  }

  /**
   * Charges the absorption well.
   *
   * The charge is what both the reach and the pull are read from, it only ever
   * grows, and it grows faster the slower the seed is. Because it accumulates
   * rather than being read off the instantaneous speed, it cannot collapse: the
   * seed slowing down leaves the well larger than it was, and once the seed has
   * stopped the well carries on swelling at full rate rather than being complete
   * the instant the seed halts.
   *
   * That is the difference from reading the power straight off the speed. The
   * seed spends the last quarter of a second of flight crossing from its
   * `stopSpeed` to a halt, so a speed-driven well has no room to ramp at all and
   * looks like it switches on at the stop. A charge has all the time in the world.
   */
  private advanceAbsorption(step: number): void {
    const speed = Math.hypot(this.motion.vx, this.motion.vy);
    const span = COLLECT_CONFIG.attractionOffSpeed - COLLECT_CONFIG.stopSpeed;
    const slowness = span <= 0 ? 1 : Math.max(0, Math.min(1, (COLLECT_CONFIG.attractionOffSpeed - speed) / span));
    const exponent = COLLECT_CONFIG.attractionExponent;
    const ease = (Math.exp(exponent * slowness) - 1) / (Math.exp(exponent) - 1);
    this.absorptionCharge = Math.min(1, this.absorptionCharge + COLLECT_CONFIG.attractionChargePerSecond * ease * step);
  }

  /**
   * The speed a pixel is pulled towards the seed at, in logical units per second.
   *
   * Falls off with distance the way a gravity well does: full strength at the
   * seed, a little under half of it halfway out, and the floor at the rim. The
   * floor means the outermost pixels still arrive rather than settling on the
   * edge of the well being pulled by nothing.
   */
  private pullAt(distance: number): number {
    const radius = this.attractionRadius();
    if (radius <= 0) return 0;
    const falloff = Math.max(0, 1 - Math.min(1, distance / radius));
    return this.attractionStrength() * (COLLECT_CONFIG.pullFloor + (1 - COLLECT_CONFIG.pullFloor) * falloff * falloff);
  }

  /** Where a pixel is right now: floating at home, or wherever attraction has taken it. */
  private pixelX(index: number): number {
    if (this.detached[index] || this.streamingFlag[index]) return this.positionX[index]!;
    return this.board.pixels[index]!.homeX + floatOffsetX(this.board.pixels[index]!, this.clock);
  }

  private pixelY(index: number): number {
    if (this.detached[index] || this.streamingFlag[index]) return this.positionY[index]!;
    return this.board.pixels[index]!.homeY + floatOffsetY(this.board.pixels[index]!, this.clock);
  }

  private gather(index: number, via: 'contact' | 'attraction' | 'stream'): void {
    if (!this.alive[index]) return;
    this.alive[index] = 0;
    this.detached[index] = 0;
    this.streamingFlag[index] = 0;
    const pixel = this.board.pixels[index]!;
    // An any-colour pixel is one of every colour at once, so it is counted
    // separately rather than added to any anchor. It still fills the seed and
    // still counts toward the minimum: it is a real pixel, gathered the same way.
    if (pixel.wildcard) this.wildcardGatheredCount += 1;
    else this.countsByAnchor.set(pixel.anchorIndex, (this.countsByAnchor.get(pixel.anchorIndex) ?? 0) + 1);
    this.removeFromGrid(index);
    if (via === 'contact') {
      this.contactCollected += 1;
      if (this.phaseValue === 'moving') this.travelCollected += 1;
      else this.settlingCollected += 1;
    } else if (via === 'attraction') {
      this.attractionCollected += 1;
      if (this.phaseValue === 'moving') this.travelCollected += 1;
      else this.settlingCollected += 1;
    } else {
      this.streamedCollected += 1;
      this.settlingCollected += 1;
    }
  }

  private removeFromGrid(index: number): void {
    const pixel = this.board.pixels[index]!;
    const col = Math.max(0, Math.min(this.grid.cols - 1, Math.floor((pixel.homeX - this.grid.originX) / this.grid.cellSize)));
    const row = Math.max(0, Math.min(this.grid.rows - 1, Math.floor((pixel.homeY - this.grid.originY) / this.grid.cellSize)));
    const cell = this.grid.cells[row * this.grid.cols + col];
    if (!cell) return;
    const at = cell.indexOf(index);
    if (at >= 0) (cell as number[]).splice(at, 1);
  }

  /**
   * Gathers what the seed actually touched, along the segments it travelled.
   *
   * Candidates are ordered by how far along the segment they were met, with the
   * pixel index breaking ties, so the order in which simultaneous collections are
   * counted is stable and explainable.
   */
  private gatherAlongSegments(): void {
    for (let index = 0; index + 3 < this.segments.length; index += 4) {
      const x0 = this.segments[index]!;
      const y0 = this.segments[index + 1]!;
      const x1 = this.segments[index + 2]!;
      const y1 = this.segments[index + 3]!;
      const reach = this.contactRadius;
      this.fillCandidates((x0 + x1) / 2, (y0 + y1) / 2, Math.hypot(x1 - x0, y1 - y0) / 2 + reach);
      const hits: Array<{ index: number; along: number }> = [];
      for (const candidate of this.candidates) {
        if (!this.alive[candidate]) continue;
        const px = this.pixelX(candidate);
        const py = this.pixelY(candidate);
        if (distanceSqToSegment(px, py, x0, y0, x1, y1) <= reach * reach) {
          hits.push({ index: candidate, along: distanceAlong(px, py, x0, y0, x1, y1) });
        }
      }
      hits.sort((a, b) => (a.along === b.along ? a.index - b.index : a.along - b.along));
      for (const hit of hits) this.gather(hit.index, 'contact');
    }
  }

  /**
   * Detaches pixels within reach and moves every detached pixel inward.
   *
   * A pixel that has started moving never snaps back to its idle float and never
   * stalls: even if the attraction radius shrinks again after a bounce, it keeps
   * a minimum pull so it still arrives.
   */
  private updateAttraction(step: number): void {
    const radius = this.attractionRadius();
    const seedX = this.motion.x;
    const seedY = this.motion.y;

    if (radius > this.contactRadius) {
      this.fillCandidates(seedX, seedY, radius);
      for (const candidate of this.candidates) {
        if (!this.alive[candidate]) continue;
        const px = this.pixelX(candidate);
        const py = this.pixelY(candidate);
        const distance = Math.hypot(px - seedX, py - seedY);
        if (distance <= this.contactRadius) {
          this.gather(candidate, 'contact');
          continue;
        }
        if (distance <= radius) {
          this.detached[candidate] = 1;
          this.positionX[candidate] = px;
          this.positionY[candidate] = py;
          this.velocityX[candidate] = 0;
          this.velocityY[candidate] = 0;
          this.detachedList.push(candidate);
          this.removeFromGrid(candidate);
        }
      }
    }

    for (let at = this.detachedList.length - 1; at >= 0; at -= 1) {
      const index = this.detachedList[at]!;
      if (!this.alive[index]) {
        this.detachedList.splice(at, 1);
        continue;
      }
      const px = this.positionX[index]!;
      const py = this.positionY[index]!;
      const dx = seedX - px;
      const dy = seedY - py;
      const distance = Math.hypot(dx, dy);
      if (distance <= this.contactRadius) {
        this.gather(index, 'attraction');
        this.detachedList.splice(at, 1);
        continue;
      }
      // Pulled in at the rate the well allows at this distance. The well is a
      // charged quantity, so it cannot fall away as the seed slows; a pixel that
      // has started moving always keeps a pull that brings it in.
      const pull = this.pullAt(distance);
      // Never step past the seed: the pixel stops just inside the collection radius.
      const stepLength = Math.min(pull * step, Math.max(0, distance - this.contactRadius * 0.5));
      this.positionX[index] = px + (dx / distance) * stepLength;
      this.positionY[index] = py + (dy / distance) * stepLength;
    }
  }

  /**
   * The last resort: the nearest remaining pixels are chosen once and flown in
   * over a fixed, short interval.
   *
   * This is reached only at the attempt ceiling in `advance`, so it is what makes
   * "every attempt ends" true even if the minimum's colours sit beyond any
   * absorption radius the board allows. It takes only what is needed, and it
   * moves the real pixels, so their real colours are what is counted.
   */
  private enterStream(): void {
    const remaining = Math.max(0, this.minimumRequired - this.collected);
    // Pixels already on their way count toward the total, so only the shortfall
    // is fetched.
    const shortfall = Math.max(0, remaining - this.detachedList.length);
    const seedX = this.motion.x;
    const seedY = this.motion.y;
    const chosen: Array<{ index: number; distance: number }> = [];
    for (let index = 0; index < this.board.pixels.length; index += 1) {
      if (!this.alive[index] || this.detached[index]) continue;
      chosen.push({ index, distance: Math.hypot(this.pixelX(index) - seedX, this.pixelY(index) - seedY) });
    }
    chosen.sort((a, b) => (a.distance === b.distance ? a.index - b.index : a.distance - b.distance));
    for (const entry of chosen.slice(0, shortfall)) this.addToStream(entry.index, this.pixelX(entry.index), this.pixelY(entry.index));
    // Anything already detached joins the same flight, so nothing is left behind.
    for (const index of this.detachedList) this.addToStream(index, this.positionX[index]!, this.positionY[index]!);
    this.detachedList.length = 0;
    if (this.streamingList.length === 0) this.finishNow();
  }

  private addToStream(index: number, x: number, y: number): void {
    if (this.streamingFlag[index]) return;
    this.streamingFlag[index] = 1;
    this.streamingList.push(index);
    this.streamFromX[index] = x;
    this.streamFromY[index] = y;
    this.positionX[index] = x;
    this.positionY[index] = y;
    this.removeFromGrid(index);
  }

  /** Moves the streaming pixels in and, at the end of the interval, gathers them. */
  private runStream(elapsed: number): void {
    const duration = COLLECT_CONFIG.finalStreamSeconds;
    const t = Math.min(1, elapsed / duration);
    // Ease out, so the pixels arrive quickly then settle, rather than creeping.
    const eased = 1 - (1 - t) * (1 - t);
    for (const index of this.streamingList) {
      this.positionX[index] = this.streamFromX[index]! + (this.motion.x - this.streamFromX[index]!) * eased;
      this.positionY[index] = this.streamFromY[index]! + (this.motion.y - this.streamFromY[index]!) * eased;
    }
    if (t >= 1) {
      // Sorted so simultaneous arrivals are counted in a stable order.
      const arriving = [...this.streamingList].sort((a, b) => a - b);
      this.streamingList.length = 0;
      for (const index of arriving) {
        this.streamingFlag[index] = 0;
        this.gather(index, 'stream');
      }
      this.finishNow();
    }
  }

  /** Ends the attempt, whatever was gathered. Every attempt ends in a result. */
  private finishNow(): void {
    if (this.phaseValue === 'finished') return;
    this.phaseValue = 'finished';
    this.finish = {
      boardSeed: this.board.seed,
      continent: this.board.continent,
      counts: new Map(this.countsByAnchor),
      mixture: this.mixture(),
      totalCollected: this.collected,
      wildcardsGathered: this.wildcardGatheredCount,
      anyColourPixels: this.anyColourPixels,
      minimumRequired: this.minimumRequired,
      launch: this.launch,
      diagnostics: {
        travelCollected: this.travelCollected,
        settlingCollected: this.settlingCollected,
        contactCollected: this.contactCollected,
        attractionCollected: this.attractionCollected,
        streamedCollected: this.streamedCollected,
        durationSeconds: this.clock,
        bounces: this.bounceCount,
        stoppedAfterSeconds: this.stoppedAfter,
      },
    };
  }

  /** Ends the attempt early, for teardown or a forced finish. Gathered pixels are kept. */
  finishEarly(): CollectResult {
    this.finishNow();
    return this.finish!;
  }
}

/**
 * How far along a segment the pixel's closest point lies, used to order hits so
 * that ties are resolved in a stable, explainable way.
 */
function distanceAlong(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq <= 1e-12) return 0;
  const t = Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / lengthSq));
  return t * Math.sqrt(lengthSq);
}

/**
 * Collects grid candidates around a point into `out`, without allocating.
 *
 * Kept separate from `queryGridInto` so the hot loop reads clearly, and so the
 * candidate list is reused between calls.
 */
function collectCandidates(grid: SpatialGrid, out: number[], x: number, y: number, radius: number): number[] {
  out.length = 0;
  const reach = radius + grid.maxFloatAmplitude;
  const minCol = Math.max(0, Math.floor((x - reach - grid.originX) / grid.cellSize));
  const maxCol = Math.min(grid.cols - 1, Math.floor((x + reach - grid.originX) / grid.cellSize));
  const minRow = Math.max(0, Math.floor((y - reach - grid.originY) / grid.cellSize));
  const maxRow = Math.min(grid.rows - 1, Math.floor((y + reach - grid.originY) / grid.cellSize));
  for (let row = minRow; row <= maxRow; row += 1) {
    for (let col = minCol; col <= maxCol; col += 1) {
      const cell = grid.cells[row * grid.cols + col];
      if (!cell) continue;
      for (const index of cell) out.push(index);
    }
  }
  return out;
}
